import React, { useState } from "react";
import { Calendar, Apple, ChevronDown } from "lucide-react";
import { downloadIcs, googleCalUrl, outlookCalUrl } from "../lib/calendar";

/**
 * Tiny "Add to Calendar" dropdown for events / meetings.
 * Pass an event object: { title, description, location, start, end, url }.
 * Renders inline with the rest of the event card actions.
 */
export default function AddToCalendarButton({ event, className = "", "data-testid": testId }) {
  const [open, setOpen] = useState(false);
  if (!event?.start) return null;

  const handleApple = (e) => {
    e.preventDefault();
    downloadIcs(event);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId || "add-to-calendar-btn"}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-sm border border-navy/20 text-navy hover:bg-pearl bg-white"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Calendar size={13} />
        Add to calendar
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <button
            aria-label="Close calendar menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 cursor-default"
            tabIndex={-1}
          />
          <div
            data-testid="add-to-calendar-menu"
            role="menu"
            className="absolute right-0 mt-1 w-52 bg-white border border-navy/15 rounded-md shadow-xl z-40 py-1"
          >
            <a
              href={googleCalUrl(event)}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid="cal-google"
              className="flex items-center gap-2 px-3 py-2 text-sm text-navy hover:bg-pearl"
              role="menuitem"
            >
              <span className="w-4 h-4 rounded-sm bg-gradient-to-br from-blue-500 to-yellow-400 inline-block" />
              Google Calendar
            </a>
            <a
              href={outlookCalUrl(event)}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid="cal-outlook"
              className="flex items-center gap-2 px-3 py-2 text-sm text-navy hover:bg-pearl"
              role="menuitem"
            >
              <span className="w-4 h-4 rounded-sm bg-blue-700 inline-block" />
              Outlook (Web)
            </a>
            <button
              type="button"
              onClick={handleApple}
              data-testid="cal-apple-ics"
              className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-navy hover:bg-pearl"
              role="menuitem"
            >
              <Apple size={14} className="text-navy/70" />
              Apple Calendar (.ics)
            </button>
            <div className="text-[10px] text-navy/50 px-3 py-1 border-t border-navy/10 mt-1">
              Auto-detects your timezone · 30-min reminder included
            </div>
          </div>
        </>
      )}
    </div>
  );
}
